import { MouseEvent, PointerEvent, useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { defineMessages, useIntl } from "react-intl";
import { IconMenu2, IconShoppingCart, IconUserCircle, IconWorld, IconX, IconHeart } from "@tabler/icons-react";
import { classNames, useAppConfig } from "@ct-react/core";
import { LangLink, LocaleLink, useLocaleContext, useLocaleFormatter, useLocaleNavigate } from "@ct-react/locale";
import { useVisitor } from "@ct-react/visitor";
import { ArticleType } from "@shared/url";
import { allLocales } from "@shared/locale";
import { actions, articleTypes, routes } from "../../i18n/sharable-defs";
import { useElementSize } from "../../hooks/dom";
import { useCartCount } from "../../hooks/cart";
import { Wrapper } from "./wrapper";
import Logo from "../../assets/componable-svgs/logo-verbier-rentals.svg";
import { useWishlist } from "../../contexts/wishlist";
import "./header.scss";

const transDefs = defineMessages({
  rental: { id: "header.menu.main-rental", defaultMessage: "Locations" },
  profile: { id: "header.menu.profile", defaultMessage: "Mon profil" },
});

const Header = () => {

  const intl = useIntl();
  const { options: { withLogin } } = useAppConfig();
  const { isLoggedIn } = useVisitor();
  const location = useLocation();
  const { locale } = useLocaleContext();
  const navigate = useLocaleNavigate();
  const { transLib } = useLocaleFormatter();
  const cartCount = useCartCount();
  const { countItem } = useWishlist();

  const [ openMenu, setOpenMenu ] = useState<boolean>(false);
  const { ref, size } = useElementSize();

  useLayoutEffect(() => {
    !!size?.height && document.documentElement.style.setProperty("--cgv-header-height", `${size.height}px`);
  }, [ size?.height ])

  const target = useCallback((logout: boolean = false) => {
    const path = logout && location.pathname.match(/\/me\//)
      ? `/${locale.basename}`
      : location.pathname + location.search + location.hash;
    return encodeURIComponent(path);
  }, [ location ]);

  // ugly ios fix
  const onSubTriggerClicked = (e: MouseEvent) => {
    const checkbox = (e.target as HTMLElement).closest(".with-subs")?.querySelector(".subs-state") as HTMLInputElement;
    if (!checkbox) return;
    checkbox.checked = !checkbox.checked;
    const outsideClickEvent = (e: any) => {
      if (!!(e.target as HTMLElement).closest(".sub-nav")) return;
      checkbox.checked = false;
      document.removeEventListener("click", outsideClickEvent);
    };
    setTimeout(() => document.addEventListener("click", outsideClickEvent), 500);
  }

  const menuRendering = useMemo(() => (
    <ul>
      <li className="with-subs">
        <input type="checkbox" className="subs-state" />
        <span className="label" tabIndex={0}
              onClick={e => onSubTriggerClicked(e)}>
          {intl.formatMessage(transDefs.rental)}
        </span>
        <div className="sub-nav">
          <ul>
            <li>
              <LocaleLink to="/rentals/short" className="label"
                          onClick={() => { openMenu && setOpenMenu(false); }}>
                {intl.formatMessage(articleTypes[ArticleType.RENTAL])}
              </LocaleLink>
            </li>
            <li>
              <LocaleLink to="/rentals/seasonally" className="label"
                          onClick={() => { openMenu && setOpenMenu(false); }}>
                {intl.formatMessage(articleTypes[ArticleType.SEASONAL])}
              </LocaleLink>
            </li>
            <li>
              <LocaleLink to="/rentals/annually" className="label"
                          onClick={() => { openMenu && setOpenMenu(false); }}>
                {intl.formatMessage(articleTypes[ArticleType.ANNUAL])}
              </LocaleLink>
            </li>
          </ul>
        </div>
      </li>
      <li>
        <LocaleLink to="/purchases" className="label"
                    onClick={() => { openMenu && setOpenMenu(false); }}>
          {intl.formatMessage(articleTypes[ArticleType.SALE])}
        </LocaleLink>
      </li>
      <li>
        <LocaleLink to="/useful-info" className="label"
                    onClick={() => { openMenu && setOpenMenu(false); }}>
          {intl.formatMessage(routes.useful_info)}
        </LocaleLink>
      </li>
      <li>
        <LocaleLink to="/contact" className="label"
                    onClick={() => { openMenu && setOpenMenu(false); }}>
          {intl.formatMessage(routes.contact)}
        </LocaleLink>
      </li>
    </ul>), [ intl, openMenu ]);

  const navClasses = classNames("main-nav", { opened: openMenu });

  return (
    <header ref={ref}
            className="main-header">
      <Wrapper.Centered>

        <div className="brand keep">
          <LocaleLink to={"/"}>
            <Logo/>
          </LocaleLink>
        </div>

        <nav className={navClasses}>

          {menuRendering}

          {withLogin &&
            <div className="with-subs">
              <input type="checkbox" className="subs-state" />
              <button type="button" className="label iconized keep"
                      onClick={onSubTriggerClicked}>
                <IconUserCircle className={classNames({ connected: isLoggedIn })} />
              </button>
              <div className="sub-nav">
                {isLoggedIn
                  ? <ul>
                    <li>
                      <LocaleLink to="/me/profile" className="label">
                        {intl.formatMessage(transDefs.profile)}
                      </LocaleLink>
                    </li>
                    <li>
                      <a className="label" href={`/logout?target=${target(true)}`}>
                        {intl.formatMessage(actions.logout)}
                      </a>
                    </li>
                  </ul>
                  : <ul>
                    <li>
                      <a className="label" href={`/signup?target=${target()}`}>
                        {intl.formatMessage(actions.register)}
                      </a>
                    </li>
                    <li>
                      <a className="label" href={`/login?target=${target()}`}>
                        {intl.formatMessage(actions.login)}
                      </a>
                    </li>
                  </ul>
                }
              </div>
            </div>
          }

          <button type="button" className="iconized keep wishlist-icon"
                  onClick={() => navigate("/wishlist/")}>
            <IconHeart />
            {!!countItem()&&
            <span>
              {countItem()}
            </span>
            }
          </button>

          <button type="button" className="iconized keep"
                  onClick={() => navigate("/checkout/")}
                  >
            <IconShoppingCart />
            {cartCount > 0 && <span className="badge">{cartCount}</span>}
          </button>

          <div className="with-subs right">
            <input type="checkbox" className="subs-state" />
            <button type="button" className="label iconized keep"
                    onClick={onSubTriggerClicked}>
              <IconWorld />
            </button>
            <div className="sub-nav chipped">
              <ul>
                {allLocales.map((l, index) => (
                  <li key={index}
                      className={classNames("label", { active: locale.code === l.code})}>
                    <LangLink locale={l}>{l.basename}</LangLink>
                  </li>)
                )}
              </ul>
            </div>
          </div>

          <button type="button"
                  className="burger iconized keep"
                  onClick={() => setOpenMenu(prev => !prev)}>
            {openMenu ? <IconX /> : <IconMenu2 />}
          </button>

        </nav>

      </Wrapper.Centered>
    </header>);

}

export default Header;
